import {
  useCallback,
  useEffect,
  useState,
} from 'react'
import {
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  getApiErrorMessage,
  pipedreamAppsService,
} from '../services/api'
import type {
  PipedreamAppConnectionResponse,
} from '../client'
import {
  OnboardingEvents,
  trackEvent,
} from '../analytics/posthog'

export const PIPEDREAM_CONNECTIONS_QUERY_KEY = ['pipedream-connections']

export interface UsePipedreamConnections {
  connections: PipedreamAppConnectionResponse[]
  /**
   * The FIRST read only. A refresh deliberately does not raise this: the
   * featured tiles fold it into each tile's own loading state, so a shared
   * refresh flag would put all four tiles in a spinner because one of them was
   * disconnected.
   */
  loading: boolean
  /**
   * The last failed read, held until one succeeds — NOT React Query's live
   * error. A refetch of an errored query drops it back to `pending`, so the
   * live error vanishes the instant a retry starts, taking with it whatever
   * surface was rendering it — including the "Try again" button the user just
   * pressed. This survives the retry and clears when a read finally lands.
   */
  error: string | null
  /**
   * Re-read, returning what was loaded — or `null` when the read itself
   * failed. That distinction is what lets a settled connect answer "unknown"
   * rather than reporting a connect as failed because we could not check.
   */
  refresh: () => Promise<PipedreamAppConnectionResponse[] | null>
  /**
   * Drop these connections and report one `pipedream_app_disconnected` per
   * distinct app. Throws on the first failure — the caller owns where that
   * message is shown, and the rows that did succeed are already gone.
   *
   * Takes rows rather than one id because the two callers disagree about what
   * "disconnect" means on their surface: the catalog lists every account
   * separately and drops one, a featured tile presents the app as one thing and
   * drops all of them. The request, the event and the error copy are the same
   * either way, so only the selection differs.
   */
  disconnect: (rows: PipedreamAppConnectionResponse[]) => Promise<void>
}

/**
 * The user's connected Pipedream apps, read once for the whole page.
 *
 * Both surfaces that need this list live on Settings → Integrations at the
 * same time and behind the same capability flag — the featured category tiles
 * and the "Connected apps" / catalog card — so two independent reads meant two
 * of every request behind it (auth, a per-user PostHog flag evaluation, the
 * query) on every visit, and two copies of the answer that could disagree
 * after a connect on either one.
 *
 * React Query's shared key collapses concurrent mounts into one request and
 * gives every consumer the same answer, so a refresh from any surface updates
 * all of them. Same pattern, and same reason, as `useReferralStatus`.
 */
export function usePipedreamConnections(
  { enabled = true }: { enabled?: boolean } = {},
): UsePipedreamConnections {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: PIPEDREAM_CONNECTIONS_QUERY_KEY,
    queryFn: () => pipedreamAppsService.listConnections(),
    enabled,
  })

  const [stickyError, setStickyError] = useState<string | null>(null)
  useEffect(() => {
    if (query.isError) {
      setStickyError(
        getApiErrorMessage(query.error, 'Could not load connected apps.'),
      )
    } else if (query.isSuccess) {
      setStickyError(null)
    }
  }, [query.isError, query.isSuccess, query.error])

  const refresh = useCallback(async () => {
    if (!enabled) return []
    try {
      return await queryClient.fetchQuery({
        queryKey: PIPEDREAM_CONNECTIONS_QUERY_KEY,
        queryFn: () => pipedreamAppsService.listConnections(),
        // `fetchQuery` serves the cache when the data is still fresh, and the
        // app-wide default is a five-minute `staleTime` — so without this a
        // refresh moments after a connect would hand back the list from BEFORE
        // it, which is exactly the question being asked. Every caller here is
        // asking because something just changed on the server.
        staleTime: 0,
      })
    } catch {
      return null
    }
  }, [enabled, queryClient])

  const disconnect = useCallback(async (
    rows: PipedreamAppConnectionResponse[],
  ) => {
    // Concurrent, not sequential: the multi-account case is the whole reason
    // this takes a list, and N serialized round-trips is N times the wait for
    // no benefit. `Promise.all` rejects on the first failure exactly as an
    // awaited loop did, leaving the rest connected.
    await Promise.all(rows.map((row) => pipedreamAppsService.disconnect(row.id)))
    for (const slug of new Set(rows.map((row) => row.app_slug))) {
      const row = rows.find((candidate) => candidate.app_slug === slug)
      trackEvent(OnboardingEvents.PIPEDREAM_APP_DISCONNECTED, {
        app_slug: slug,
        app_name: row?.name,
      })
    }
  }, [])

  return {
    connections: query.data ?? [],
    // `isPending` stays true forever on a disabled query, which would leave
    // every tile spinning for a user without the capability flag.
    loading: enabled && query.isPending,
    error: stickyError,
    refresh,
    disconnect,
  }
}
